/**
 * GraphControls component - Control buttons for graph zoom and navigation.
 *
 * Provides floating controls for zoom in/out, fit to screen, and center graph.
 */

import { FC } from 'react';
import { IconButton, Tooltip, Paper } from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  CenterFocusStrong as CenterIcon,
} from '@mui/icons-material';
import type { Core } from 'cytoscape';

export interface GraphControlsProps {
  /** Reference to the graph container element */
  containerRef: React.RefObject<HTMLDivElement>;
}

export const GraphControls: FC<GraphControlsProps> = ({ containerRef }) => {
  /**
   * Get Cytoscape instance from container
   */
  const getCyInstance = (): Core | null => {
    if (!containerRef.current) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (containerRef.current as any).cytoscapeInstance || null;
  };

  /**
   * Zoom in
   */
  const handleZoomIn = () => {
    const cy = getCyInstance();
    if (cy) {
      const currentZoom = cy.zoom();
      cy.zoom({
        level: currentZoom * 1.2,
        renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 },
      });
    }
  };

  /**
   * Zoom out
   */
  const handleZoomOut = () => {
    const cy = getCyInstance();
    if (cy) {
      const currentZoom = cy.zoom();
      cy.zoom({
        level: currentZoom * 0.8,
        renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 },
      });
    }
  };

  /**
   * Fit graph to screen
   */
  const handleFitToScreen = () => {
    const cy = getCyInstance();
    if (cy) {
      cy.fit(undefined, 30); // 30px padding
    }
  };

  /**
   * Center graph
   */
  const handleCenter = () => {
    const cy = getCyInstance();
    if (cy) {
      cy.center();
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        top: 16,
        right: 16,
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        p: 0.5,
        backgroundColor: 'background.paper',
        borderRadius: 1,
      }}
    >
      <Tooltip title="Zoom In" placement="left">
        <IconButton
          size="small"
          onClick={handleZoomIn}
          sx={{
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Zoom Out" placement="left">
        <IconButton
          size="small"
          onClick={handleZoomOut}
          sx={{
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ZoomOutIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Fit to Screen" placement="left">
        <IconButton
          size="small"
          onClick={handleFitToScreen}
          sx={{
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <FitScreenIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Center Graph" placement="left">
        <IconButton
          size="small"
          onClick={handleCenter}
          sx={{
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <CenterIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Paper>
  );
};
